import React, { useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    IconButton,
    FormControlLabel,
    Switch,
    CircularProgress,
    ToggleButton,
    ToggleButtonGroup,
    Divider,
} from '@mui/material';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import CloseIcon from '@mui/icons-material/Close';
import RequestService from '../../services/RequestService';

const initialState = {
    title: '',
    description: '',
    startsAt: '',
    endsAt: '',
    active: true,
    banner: null,
    placeName: '',
    placeStreet: '',
    placeNumber: '',
    placeNeighborhood: '',
    placeCity: '',
    placeZip: '',
};

export default function NewEventDialog({ open, onClose, onSuccess, user }) {
    const [formData, setFormData] = useState(initialState);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [hasPlace, setHasPlace] = useState(false);

    const bannerPreview = useMemo(() => {
        if (!formData.banner) return null;
        return URL.createObjectURL(formData.banner);
    }, [formData.banner]);

    const resetForm = () => {
        setFormData(initialState);
        setError(null);
        setSubmitting(false);
    };

    const handleClose = (event, reason) => {
        // se você quiser manter o padrão "não fechar ao clicar fora"
        if (reason === 'backdropClick') return;
        resetForm();
        onClose?.(event, reason);
    };

    const handleChange = (e) => {
        const { name, value, type, files, checked } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]:
                type === 'file'
                    ? files?.[0] || null
                    : type === 'checkbox'
                      ? checked
                      : value,
        }));
    };

    // datetime-local normalmente vem "YYYY-MM-DDTHH:mm"
    // LocalDateTime geralmente aceita "YYYY-MM-DDTHH:mm:ss"
    const normalizeLocalDateTime = (value) => {
        if (!value) return '';
        return value.length === 16 ? `${value}:00` : value;
    };

    const validate = () => {
        if (!formData.title.trim()) return 'Informe um título.';
        if (!formData.description.trim()) return 'Informe uma descrição.';

        if (formData.startsAt && formData.endsAt) {
            const s = new Date(formData.startsAt);
            const e = new Date(formData.endsAt);
            if (e < s) return 'A data final não pode ser menor que a inicial.';
        }

        // se quiser exigir start quando tiver end:
        if (!formData.startsAt && formData.endsAt) {
            return 'Informe a data de início (startsAt).';
        }

        return null;
    };

    const handleSubmit = async () => {
        setError(null);
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }

        try {
            setSubmitting(true);

            const fd = new FormData();
            fd.append('title', formData.title.trim());
            fd.append('description', formData.description.trim());

            if (formData.startsAt) {
                fd.append(
                    'startsAt',
                    normalizeLocalDateTime(formData.startsAt)
                );
            }
            if (formData.endsAt) {
                fd.append('endsAt', normalizeLocalDateTime(formData.endsAt));
            }

            // backend expects Boolean; multipart sends string fine
            fd.append('active', String(formData.active));

            if (formData.banner) {
                fd.append('banner', formData.banner); // ✅ key must be "banner"
            }
            if (hasPlace) {
                if (formData.placeName)
                    fd.append('placeName', formData.placeName);
                if (formData.placeStreet)
                    fd.append('placeStreet', formData.placeStreet);
                if (formData.placeNumber)
                    fd.append('placeNumber', formData.placeNumber);
                if (formData.placeNeighborhood)
                    fd.append('placeNeighborhood', formData.placeNeighborhood);
                if (formData.placeCity)
                    fd.append('placeCity', formData.placeCity);
                if (formData.placeState)
                    fd.append('placeState', formData.placeState);
                if (formData.placeZip) fd.append('placeZip', formData.placeZip);
            }

            // Ajuste para o seu método real:
            // - se seu backend é POST /api/v1/events (pegando companyId do token),
            //   basta RequestService.createEvent(fd)
            // - se você incluiu companyId no endpoint, passe também.
            await RequestService.createEvent(fd);

            onSuccess?.('Evento criado com sucesso');
            resetForm();
            onClose?.();
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.response?.data ||
                'Erro ao criar o evento. Verifique os dados.';
            setError(
                typeof message === 'string'
                    ? message
                    : 'Erro ao criar o evento.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
            <DialogTitle>
                Novo Evento
                <IconButton
                    aria-label='close'
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 1,
                    }}
                >
                    {/* Banner picker – same style as Product */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            backgroundColor: '#F5F5F5',
                            padding: 2,
                            borderRadius: 2,
                        }}
                    >
                        <Box
                            sx={{
                                width: 120,
                                height: 120,
                                border: '1px dashed #CCCCCC',
                                borderRadius: '50%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                cursor: 'pointer',
                                overflow: 'hidden',
                            }}
                        >
                            {formData.banner ? (
                                <img
                                    src={bannerPreview}
                                    alt='Preview'
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                />
                            ) : (
                                <AddPhotoAlternateOutlinedIcon
                                    fontSize='large'
                                    color='disabled'
                                />
                            )}
                        </Box>

                        <Button
                            variant='contained'
                            component='label'
                            fullWidth
                            disabled={submitting}
                        >
                            Selecionar Banner
                            <input
                                type='file'
                                accept='image/*'
                                hidden
                                name='banner'
                                onChange={handleChange}
                            />
                        </Button>

                        <Typography variant='body2' color='text.secondary'>
                            Banner (opcional)
                        </Typography>
                    </Box>

                    <TextField
                        label='Título'
                        name='title'
                        value={formData.title}
                        onChange={handleChange}
                        fullWidth
                        disabled={submitting}
                    />

                    <TextField
                        label='Descrição'
                        name='description'
                        value={formData.description}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={4}
                        disabled={submitting}
                    />

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                            label='Início'
                            name='startsAt'
                            type='datetime-local'
                            value={formData.startsAt}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: 1, minWidth: 220 }}
                            disabled={submitting}
                        />
                        <TextField
                            label='Fim'
                            name='endsAt'
                            type='datetime-local'
                            value={formData.endsAt}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: 1, minWidth: 220 }}
                            disabled={submitting}
                        />
                    </Box>

                    <FormControlLabel
                        control={
                            <Switch
                                name='active'
                                checked={formData.active}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        active: e.target.checked,
                                    }))
                                }
                                disabled={submitting}
                            />
                        }
                        label={formData.active ? 'Ativo' : 'Inativo'}
                    />

                    <Typography sx={{ fontWeight: 600 }}>
                        Local do evento
                    </Typography>

                    <ToggleButtonGroup
                        value={hasPlace ? 'YES' : 'NO'}
                        exclusive
                        onChange={(e, v) => {
                            if (!v) return;
                            const enable = v === 'YES';
                            setHasPlace(enable);

                            // opcional: se desligou, limpa os campos
                            if (!enable) {
                                setFormData((prev) => ({
                                    ...prev,
                                    placeName: '',
                                    placeStreet: '',
                                    placeNumber: '',
                                    placeNeighborhood: '',
                                    placeCity: '',
                                    placeZip: '',
                                    placeState: '',
                                }));
                            }
                        }}
                        fullWidth
                        disabled={submitting}
                    >
                        <ToggleButton value='NO'>Sem local</ToggleButton>
                        <ToggleButton value='YES'>Inserir local</ToggleButton>
                    </ToggleButtonGroup>

                    {hasPlace ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <TextField
                                label='Nome do local'
                                name='placeName'
                                value={formData.placeName}
                                onChange={handleChange}
                                fullWidth
                                disabled={submitting}
                            />

                            <TextField
                                label='Rua'
                                name='placeStreet'
                                value={formData.placeStreet}
                                onChange={handleChange}
                                fullWidth
                                disabled={submitting}
                            />

                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <TextField
                                    label='Número'
                                    name='placeNumber'
                                    value={formData.placeNumber}
                                    onChange={handleChange}
                                    sx={{ flex: 1, minWidth: 140 }}
                                    disabled={submitting}
                                />
                                <TextField
                                    label='Bairro'
                                    name='placeNeighborhood'
                                    value={formData.placeNeighborhood}
                                    onChange={handleChange}
                                    sx={{ flex: 2, minWidth: 220 }}
                                    disabled={submitting}
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <TextField
                                    label='Cidade'
                                    name='placeCity'
                                    value={formData.placeCity}
                                    onChange={handleChange}
                                    sx={{ flex: 2, minWidth: 220 }}
                                    disabled={submitting}
                                />
                                <TextField
                                    label='CEP'
                                    name='placeZip'
                                    value={formData.placeZip}
                                    onChange={handleChange}
                                    sx={{ flex: 1, minWidth: 160 }}
                                    disabled={submitting}
                                />
                            </Box>

                            <TextField
                                label='Estado'
                                name='placeState'
                                value={formData.placeState || ''}
                                onChange={handleChange}
                                fullWidth
                                disabled={submitting}
                            />
                        </Box>
                    ) : null}

                    {error && <Alert severity='error'>{error}</Alert>}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleClose}
                    variant='outlined'
                    disabled={submitting}
                >
                    Cancelar
                </Button>
                <Button
                    onClick={handleSubmit}
                    color='primary'
                    variant='contained'
                    disabled={submitting}
                    endIcon={submitting ? <CircularProgress size={18} /> : null}
                >
                    {submitting ? 'Salvando...' : 'Criar Evento'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
